import { useEffect, useState } from 'react';
import { api } from './api.js';

interface Brief { brief_date: string; headline: string; content_md: string; focus_today: string[]; model: string; generated_at: string }
interface Weekly { week_start: string; headline: string; content_md: string; on_track: boolean;
  adjustments_json: Array<{ area: string; change: string; why: string }>;
  watch_items: string[]; coming_up: string[]; generated_at: string }
interface Usage { monthToDateUsd: number; budgetUsd: number; breakdown: Array<Record<string, string>> }

export function Coach(): JSX.Element {
  const [brief, setBrief] = useState<Brief | null>(null);
  const [weekly, setWeekly] = useState<Weekly[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = (): void => {
    void api<Brief | null>('/coach/brief').then(setBrief).catch(() => undefined);
    void api<Weekly[]>('/coach/weekly').then(setWeekly).catch(() => undefined);
    void api<Usage>('/coach/usage').then(setUsage).catch(() => undefined);
  };
  useEffect(reload, []);

  async function run(kind: 'brief' | 'weekly'): Promise<void> {
    setBusy(kind); setErr(null);
    try {
      await api(`/coach/${kind}${kind === 'brief' ? '?force=true' : ''}`, { method: 'POST' });
      reload();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <>
      {err && <div className="card"><p className="bad">{err}</p></div>}

      <div className="card">
        <h2>Today&apos;s brief</h2>
        {brief ? (
          <>
            <p className="headline">{brief.headline}</p>
            <p className="body">{brief.content_md}</p>
            {brief.focus_today?.length > 0 && (
              <ul className="focus">{brief.focus_today.map((f) => <li key={f}>{f}</li>)}</ul>
            )}
            <p className="muted small">{brief.model} · {new Date(brief.generated_at).toLocaleString()}</p>
          </>
        ) : <p className="muted">No brief yet for today.</p>}
        <button type="button" disabled={busy === 'brief'} onClick={() => void run('brief')}>
          {busy === 'brief' ? 'Thinking…' : brief ? 'Regenerate' : 'Generate brief'}
        </button>
      </div>

      <div className="card">
        <h2>Weekly review</h2>
        {weekly[0] ? (
          <>
            <p className="headline">
              {weekly[0].headline}{' '}
              <span className={weekly[0].on_track ? 'good' : 'bad'}>
                {weekly[0].on_track ? 'on track' : 'off track'}
              </span>
            </p>
            <p className="body">{weekly[0].content_md}</p>
            {weekly[0].adjustments_json?.length > 0 && (
              <>
                <h3>Adjustments</h3>
                <ul className="focus">
                  {weekly[0].adjustments_json.map((a, i) => (
                    <li key={i}><strong>{a.area.replace(/_/g, ' ')}:</strong> {a.change}<br /><span className="muted small">{a.why}</span></li>
                  ))}
                </ul>
              </>
            )}
            {weekly[0].watch_items?.length > 0 && (
              <><h3>Watch</h3><ul className="focus">{weekly[0].watch_items.map((w) => <li key={w}>{w}</li>)}</ul></>
            )}
          </>
        ) : <p className="muted">No weekly review yet.</p>}
        <button type="button" disabled={busy === 'weekly'} onClick={() => void run('weekly')}>
          {busy === 'weekly' ? 'Analysing…' : 'Run weekly review'}
        </button>
      </div>

      {usage && (
        <div className="card muted-card">
          <h2>AI spend this month</h2>
          <p className="muted small">
            ${usage.monthToDateUsd.toFixed(4)} of ${usage.budgetUsd}. The budget is checked
            before each call is made, not after the bill arrives.
          </p>
        </div>
      )}
    </>
  );
}
